import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Overview, PlatformInsights } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { Badge, CardHead, Stat, StatGroup, StatusBadge, ErrorBanner, Empty, SkeletonBlock } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { VolumeChart, Sparkline, PieDonut, HourChart, MarginChart, DayBars } from '../../components/charts/Charts.js';
import { formatDateTime, formatDuration, formatMoney, formatNumber, relativeTime } from '../../lib/format.js';
import { useLiveState } from '../live/useLiveState.js';

/**
 * The platform's own dashboard: every account at once, for platform staff (owner, 21 October
 * 2026: "more metrics added, graphs, pie charts"). /overview gives the tiles and the accounts;
 * /overview/insights the charts, over the same rolling window, so the two add up.
 *
 * Below the call figures, what someone running the platform looks for (31 October 2026): margin,
 * phones online by day, the switch and its Teams link, certificates, SIP bans and fraud holds, and
 * the email outbox. Each is a card; only a problem -- the switch silent, Teams down, a certificate
 * within a fortnight, email failing -- is a banner at the top.
 */

/** A certificate this close to running out is a problem: Let's Encrypt renews 30 days ahead. */
const CERT_WARN_DAYS = 14;

const certTone = (days: number | null): 'ok' | 'warn' | 'danger' | 'muted' =>
  days === null ? 'muted' : days <= 3 ? 'danger' : days <= CERT_WARN_DAYS ? 'warn' : 'ok';

/** Load, memory and disk read against the same thresholds: full is a problem, nearly full a warning. */
const shareTone = (pct: number | null): 'ok' | 'warn' | 'danger' | undefined =>
  pct === null ? undefined : pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : 'ok';

const uptimeText = (sec: number | null): string => {
  if (sec === null) return '—';
  const d = Math.floor(sec / 86_400);
  const h = Math.floor((sec % 86_400) / 3_600);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((sec % 3_600) / 60)}m`;
};

const OUTCOMES: Record<PlatformInsights['outcomes'][number]['outcome'], { label: string; colour: string }> = {
  answered: { label: 'Answered', colour: 'var(--arc-ok)' },
  cancelled: { label: 'Caller hung up', colour: 'var(--arc-fg-4)' },
  no_answer: { label: 'No answer', colour: 'var(--arc-warn)' },
  busy: { label: 'Busy', colour: 'var(--arc-c4)' },
  failed: { label: 'Failed', colour: 'var(--arc-danger)' },
};

const pct = (n: number, of: number): string => (of > 0 ? `${Math.round((n / of) * 1000) / 10}%` : '—');

export function PlatformDashboard() {
  const can = useSession((s) => s.can);
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<Overview>('/v1/overview'),
    refetchInterval: 60_000,
  });
  const insights = useQuery({
    queryKey: ['overview-insights'],
    queryFn: () => api.get<PlatformInsights>('/v1/overview/insights?days=30'),
    refetchInterval: 60_000,
  });

  const t = overview.data?.totals;
  const i = insights.data;
  const tz = i?.window.timezone ?? 'Africa/Johannesburg';
  const currency = t?.currency ?? 'ZAR';
  const regBy = new Map((i?.registrations ?? []).map((r) => [r.tenant_id, r]));
  const online = i?.registrations?.reduce((n, r) => n + r.online, 0);
  const recent = i?.registrations?.reduce((n, r) => n + r.recent, 0) ?? 0;
  const outcomeTotal = (i?.outcomes ?? []).reduce((n, o) => n + o.calls, 0);
  const outcomes = (Object.keys(OUTCOMES) as Array<keyof typeof OUTCOMES>)
    .map((k) => ({ key: k, ...OUTCOMES[k], calls: i?.outcomes.find((o) => o.outcome === k)?.calls ?? 0 }))
    .filter((o) => o.calls > 0);
  const busiestHour = i?.hours.reduce<PlatformInsights['hours'][number] | null>((b, h) => (!b || h.total > b.total ? h : b), null);
  const troubled = (i?.trunks ?? []).filter((tr) => tr.health === 'down' || tr.registration === 'failing');
  const accounts = overview.data?.accounts ?? [];
  const maxCalls = Math.max(1, ...accounts.map((a) => a.calls));
  const spendTotal = Number(t?.spend ?? 0);

  // The problems that earn a banner. Everything else waits in its card.
  const silentNodes = (i?.nodes ?? []).filter((n) => n.liveness !== 'up');
  const teamsDown = (i?.nodes ?? []).filter((n) => n.teams?.up === false);
  const expiring = (i?.certificates?.items ?? []).filter((x) => x.days_left !== null && x.days_left <= CERT_WARN_DAYS);
  const emailTrouble = i?.email && (i.email.failed_7d > 0 || i.email.retrying > 0);
  const regDays = i?.registrations_by_day ?? null;
  const regKnown = (regDays ?? []).filter((d) => d.online !== null);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Dashboard</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>All accounts · last 30 days · times in {tz}</p>
        </div>
      </div>

      {overview.error && <ErrorBanner error={overview.error} />}
      {insights.error && <ErrorBanner error={insights.error} />}

      {/* Problems only: a healthy platform shows no banner at all. */}
      {i?.fraud && i.fraud.open_holds > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>{i.fraud.open_holds} fraud hold{i.fraud.open_holds === 1 ? '' : 's'} open</div>
            <div style={{ fontSize: 12 }}>
              Held seats cannot call out. <Link to="/fraud" className="arc-btn arc-btn-sm" style={{ marginLeft: 6 }}>Review</Link>
            </div>
          </div>
        </div>
      )}
      {troubled.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {troubled.map((tr) => tr.name).join(', ')} {troubled.length === 1 ? 'is' : 'are'} down
            </div>
            <div style={{ fontSize: 12 }}>
              {troubled.some((tr) => tr.registration === 'failing') ? 'Registration is failing.' : 'The carrier is not answering the switch.'}
              <Link to="/inventory/carriers" className="arc-btn arc-btn-sm" style={{ marginLeft: 6 }}>Carriers</Link>
            </div>
          </div>
        </div>
      )}
      {silentNodes.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {silentNodes.map((n) => n.name).join(', ')} {silentNodes.length === 1 ? 'has' : 'have'} stopped reporting
            </div>
            <div style={{ fontSize: 12 }}>
              {silentNodes.every((n) => n.liveness === 'never') ? 'No heartbeat has ever arrived.'
                : `Last heard ${relativeTime(silentNodes.find((n) => n.last_heartbeat_at)!.last_heartbeat_at!)}.`}
              <Link to="/platform/nodes" className="arc-btn arc-btn-sm" style={{ marginLeft: 6 }}>Nodes</Link>
            </div>
          </div>
        </div>
      )}
      {teamsDown.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="teams" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>Microsoft Teams is not answering the switch</div>
            <div style={{ fontSize: 12 }}>
              Calls to and from Teams fail{teamsDown[0]!.teams?.down_since ? ` · down since ${formatDateTime(teamsDown[0]!.teams!.down_since!, tz)}` : ''}.
            </div>
          </div>
        </div>
      )}
      {expiring.length > 0 && (
        <div className={`arc-banner ${expiring.some((x) => x.days_left! <= 3) ? 'arc-banner-danger' : 'arc-banner-warn'}`} style={{ marginBottom: 12 }} role="alert">
          <Icon name="shield" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {expiring.map((x) => `${x.label} certificate: ${x.days_left} day${x.days_left === 1 ? '' : 's'} left`).join(' · ')}
            </div>
            <div style={{ fontSize: 12 }}>Renewal should have happened by now. Check certbot on the node.</div>
          </div>
        </div>
      )}
      {emailTrouble && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }} role="alert">
          <Icon name="mail" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              Email: {[i!.email!.failed_7d > 0 ? `${i!.email!.failed_7d} failed this week` : null, i!.email!.retrying > 0 ? `${i!.email!.retrying} retrying` : null].filter(Boolean).join(' · ')}
            </div>
            {i!.email!.last_failure && (
              <div style={{ fontSize: 12 }}>{i!.email!.last_failure.error} <span className="arc-subtle">· {relativeTime(i!.email!.last_failure.at)}</span></div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          <Stat bare icon="phone" hue={1} label="Calls"
            value={t ? formatNumber(t.total_calls) : '—'}
            sub={t ? `${formatNumber(t.answered_calls)} answered` : undefined} />
          <Stat bare icon="check" hue={2} label="Answer rate"
            value={t ? `${t.answer_rate_pct}%` : '—'}
            tone={t && t.total_calls > 0 ? (t.answer_rate_pct >= 70 ? 'ok' : t.answer_rate_pct >= 40 ? 'warn' : 'danger') : undefined}
            sub={t ? `${t.inbound} in · ${t.outbound} out · ${t.internal} internal` : undefined} />
          <Stat bare icon="clock" hue={4} label="Talk time"
            value={t ? formatDuration(t.total_duration_sec) : '—'}
            sub={t ? `avg ${formatDuration(t.avg_duration_sec)}` : undefined} />
          <Stat bare icon="billing" hue={3} label="Spend"
            value={formatMoney(t?.spend ?? null, currency)} sub="excluding VAT" />
        </StatGroup>
      </div>

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          <Stat bare icon="queue" hue={5} label="Most calls at once"
            value={i ? formatNumber(i.peak?.calls ?? 0) : '—'}
            sub={i?.peak ? formatDateTime(i.peak.at, tz) : i ? 'no calls' : undefined} />
          <Stat bare icon="extension" hue={2} label="Phones online"
            value={i ? (online === undefined ? '—' : formatNumber(online)) : '—'}
            sub={i ? (i.registrations === null ? 'no report from the switch' : `${recent} more seen this week`) : undefined} />
          {can('number:read') && (
            <Stat bare icon="hash" hue={1} label="Numbers in use"
              value={i?.numbers ? formatNumber(i.numbers.assigned) : '—'}
              sub={i?.numbers ? `${i.numbers.available} in stock${i.numbers.other ? ` · ${i.numbers.other} other` : ''}` : undefined} />
          )}
          <Stat bare icon="check" hue={4} label="Call quality"
            value={i?.quality.avg_mos != null ? `${i.quality.avg_mos.toFixed(2)} MOS` : '—'}
            tone={i?.quality.avg_mos != null ? (i.quality.avg_mos >= 4 ? 'ok' : i.quality.avg_mos >= 3.5 ? 'warn' : 'danger') : undefined}
            sub={i ? `${i.quality.poor} of ${i.quality.measured} below 3.5` : undefined} />
        </StatGroup>
      </div>

      <LiveNow names={new Map(accounts.map((a) => [a.id, a.name]))} />

      <div className="arc-grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', marginBottom: 14 }}>
        <div className="arc-card">
          <CardHead title="Call volume" sub="per day" />
          <div className="arc-card-body">
            {i ? <VolumeChart data={i.series} /> : <div className="arc-skeleton" style={{ height: 150 }} />}
          </div>
        </div>
        <div className="arc-card">
          <CardHead title="Outcomes" sub={i ? <span className="arc-num">{formatNumber(outcomeTotal)} calls</span> : undefined} />
          <div className="arc-card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {!i ? <div className="arc-skeleton" style={{ width: 120, height: 120, borderRadius: '50%' }} />
              : outcomeTotal === 0 ? <span className="arc-subtle">No calls</span>
              : (
                <>
                  <PieDonut size={120} data={outcomes.map((o) => ({ label: o.label, value: o.calls, color: o.colour }))} />
                  <dl className="arc-kv" style={{ gridTemplateColumns: '1fr auto auto', flex: 1, minWidth: 170, fontSize: 12.5, margin: 0 }}>
                    {outcomes.map((o) => (
                      <OutcomeRow key={o.key} swatch={o.colour} label={o.label} value={formatNumber(o.calls)} share={pct(o.calls, outcomeTotal)} />
                    ))}
                  </dl>
                </>
              )}
          </div>
        </div>
      </div>

      <div className="arc-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', marginBottom: 14 }}>
        <div className="arc-card">
          <CardHead title="Busiest hours"
            sub={busiestHour && busiestHour.total > 0 ? `peak ${String(busiestHour.hour).padStart(2, '0')}:00` : undefined} />
          <div className="arc-card-body">
            {i ? <HourChart data={i.hours} /> : <div className="arc-skeleton" style={{ height: 130 }} />}
          </div>
        </div>
        <div className="arc-card">
          <CardHead title="Daily spend" sub={formatMoney(t?.spend ?? null, currency)} />
          <div className="arc-card-body">
            {i ? <Sparkline height={130} label="Daily spend" values={i.series.map((d) => Number(d.spend) || 0)} />
              : <div className="arc-skeleton" style={{ height: 130 }} />}
          </div>
        </div>
        <div className="arc-card">
          <CardHead title="Most at once" sub="per day" />
          <div className="arc-card-body">
            {i ? <Sparkline height={130} colour="var(--arc-c4)" label="Most calls at once per day" values={i.series.map((d) => d.peak)} />
              : <div className="arc-skeleton" style={{ height: 130 }} />}
          </div>
        </div>
      </div>

      <div className="arc-grid arc-grid-2" style={{ marginBottom: 14 }}>
        {i?.trunks !== null && (
          <div className="arc-card">
            <CardHead title="Trunks" sub="health now · calls over 30 days" />
            {!i ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 60 }} /></div>
              : i.trunks!.length === 0 ? <Empty icon="carrier" title="No active trunks" />
              : (
                <div className="arc-table-wrap">
                  <table className="arc-table">
                    <thead>
                      <tr><th>Trunk</th><th>Health</th><th className="arc-td-num">Up 24h</th><th className="arc-td-num">Calls</th><th className="arc-td-num">Answered</th></tr>
                    </thead>
                    <tbody>
                      {i.trunks!.map((tr) => {
                        const calls = tr.inbound + tr.outbound;
                        return (
                          <tr key={tr.id}>
                            <td>
                              <div className="arc-stack">
                                <span style={{ fontWeight: 550 }}>{tr.name}</span>
                                <span className="arc-subtle" style={{ fontSize: 11 }}>
                                  {[tr.carrier, tr.account ?? 'platform'].filter(Boolean).join(' · ')}
                                </span>
                              </div>
                            </td>
                            <td>
                              <StatusBadge status={tr.registration === 'failing' ? 'unregistered' : tr.health} />
                            </td>
                            <td className="arc-td-num">{tr.uptime_24h_pct === null ? '—' : `${tr.uptime_24h_pct}%`}</td>
                            <td className="arc-td-num">
                              <div className="arc-stack" style={{ alignItems: 'flex-end' }}>
                                <span>{formatNumber(calls)}</span>
                                <span className="arc-subtle" style={{ fontSize: 11 }}>{tr.outbound} out · {tr.inbound} in</span>
                              </div>
                            </td>
                            <td className="arc-td-num">{pct(tr.answered, calls)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        )}

        <div className="arc-card">
          <CardHead title="Failed calls" sub="by cause, excluding no answer" />
          {!i ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 60 }} /></div>
            : i.causes.length === 0 ? <Empty icon="check" title="No failed calls" />
            : (
              <div className="arc-table-wrap">
                <table className="arc-table">
                  <thead>
                    <tr><th>Cause</th><th>Direction</th><th className="arc-td-num">Calls</th><th className="arc-td-num">Last</th></tr>
                  </thead>
                  <tbody>
                    {i.causes.map((r) => (
                      <tr key={`${r.cause}-${r.sip_code}-${r.direction}`}>
                        <td>
                          <span className="arc-mono" style={{ fontSize: 12 }}>{r.cause ?? 'unknown'}</span>
                          {r.sip_code !== null && <span className="arc-subtle arc-num" style={{ marginLeft: 6, fontSize: 11 }}>{r.sip_code}</span>}
                        </td>
                        <td className="arc-muted">{r.direction}</td>
                        <td className="arc-td-num">{formatNumber(r.calls)}</td>
                        <td className="arc-td-num arc-subtle" style={{ fontSize: 12 }}>{formatDateTime(r.last_at, tz)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>

      <div className="arc-card">
        <CardHead title="Accounts" sub={<span className="arc-num">{accounts.length} accounts</span>} />
        {!overview.data ? <div className="arc-card-body"><SkeletonBlock height={60} query={overview} /></div> : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="arc-td-num">Calls</th>
                  <th className="arc-td-num">Answered</th>
                  <th className="arc-td-num">Spend</th>
                  <th className="arc-td-num">Extensions</th>
                  <th className="arc-td-num">Phones online</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => {
                  const reg = regBy.get(a.id);
                  return (
                    <tr key={a.id}>
                      <td><Link to="/manage/$tenantId" params={{ tenantId: a.slug }} style={{ fontWeight: 550 }}>{a.name}</Link></td>
                      <td className="arc-td-num">
                        <div className="arc-stack" style={{ alignItems: 'flex-end', gap: 3 }}>
                          <span>{formatNumber(a.calls)}</span>
                          <span className="arc-meter" aria-hidden="true" style={{ width: 80 }}>
                            <span style={{ width: `${(a.calls / maxCalls) * 100}%`, background: 'var(--arc-c1)' }} />
                          </span>
                        </div>
                      </td>
                      <td className="arc-td-num">{pct(a.answered, a.calls)}</td>
                      <td className="arc-td-num">
                        <div className="arc-stack" style={{ alignItems: 'flex-end' }}>
                          <span>{formatMoney(a.spend, a.currency ?? currency)}</span>
                          {a.spend !== null && spendTotal > 0 && (
                            <span className="arc-subtle" style={{ fontSize: 11 }}>{pct(Number(a.spend), spendTotal)} of spend</span>
                          )}
                        </div>
                      </td>
                      <td className="arc-td-num">{a.active_extensions} / {a.extensions}</td>
                      <td className="arc-td-num">{i?.registrations === null || !i ? '—' : formatNumber(reg?.online ?? 0)}</td>
                      <td><StatusBadge status={a.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Margin: what the calls sold for against what the carrier charged, by day and by account. */}
      <div className="arc-grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', marginTop: 14, marginBottom: 14 }}>
        <div className="arc-card">
          <CardHead title="Margin"
            sub={i ? (
              <span className="arc-num">
                {formatMoney(i.margin.margin, currency)} on {formatMoney(i.margin.sell, currency)} sold
                {i.margin.margin_pct !== null ? ` · ${i.margin.margin_pct}%` : ''}
                {i.margin.unpriced > 0 ? ` · ${i.margin.unpriced} call${i.margin.unpriced === 1 ? '' : 's'} without a cost` : ''}
              </span>
            ) : undefined} />
          <div className="arc-card-body">
            {!i ? <div className="arc-skeleton" style={{ height: 150 }} />
              : i.margin.sold_calls === 0 ? <div className="arc-subtle">No calls sold in this window.</div>
              : <MarginChart currency={currency} data={i.series.map((d) => ({ day: d.day, sell: Number(d.sell) || 0, cost: Number(d.cost) || 0 }))} />}
          </div>
        </div>
        <div className="arc-card">
          <CardHead title="Margin by account" sub="calls we sold" />
          {!i ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 60 }} /></div>
            : i.margin.accounts.length === 0 ? <Empty icon="billing" title="Nothing sold yet" />
            : (
              <div className="arc-table-wrap">
                <table className="arc-table">
                  <thead>
                    <tr><th>Account</th><th className="arc-td-num">Sold</th><th className="arc-td-num">Cost</th><th className="arc-td-num">Margin</th></tr>
                  </thead>
                  <tbody>
                    {i.margin.accounts.map((a) => (
                      <tr key={a.tenant_id}>
                        <td>
                          <div className="arc-stack">
                            <Link to="/manage/$tenantId" params={{ tenantId: a.slug }} style={{ fontWeight: 550 }}>{a.name}</Link>
                            <span className="arc-subtle" style={{ fontSize: 11 }}>{formatNumber(a.sold_calls)} calls{a.unpriced > 0 ? ` · ${a.unpriced} without a cost` : ''}</span>
                          </div>
                        </td>
                        <td className="arc-td-num" style={{ whiteSpace: 'nowrap' }}>{formatMoney(a.sell, currency)}</td>
                        <td className="arc-td-num" style={{ whiteSpace: 'nowrap' }}>{formatMoney(a.cost, currency)}</td>
                        <td className="arc-td-num" style={{ whiteSpace: 'nowrap' }}>
                          <div className="arc-stack" style={{ alignItems: 'flex-end' }}>
                            <span style={Number(a.margin) < 0 ? { color: 'var(--arc-danger)' } : undefined}>{formatMoney(a.margin, currency)}</span>
                            {a.margin_pct !== null && <span className="arc-subtle" style={{ fontSize: 11 }}>{a.margin_pct}%</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>

      {/* Phones over time, and what the edge and the fraud rules refused. */}
      <div className="arc-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', marginBottom: 14 }}>
        <div className="arc-card">
          <CardHead title="Phones online" sub="at the end of each day" />
          <div className="arc-card-body">
            {!i ? <div className="arc-skeleton" style={{ height: 110 }} />
              : regDays === null ? <div className="arc-subtle" style={{ fontSize: 12 }}>No record from the switch.</div>
              : regKnown.length < 2 ? <div className="arc-subtle" style={{ fontSize: 12 }}>Not enough history yet · {online ?? 0} online now</div>
              : (
                <>
                  <Sparkline height={110} colour="var(--arc-c2)" label="Phones online at the end of each day" values={regKnown.map((d) => d.online!)} />
                  <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
                    <span className="arc-subtle">{formatDateTime(regKnown[0]!.day + 'T12:00:00Z', tz).split(',')[0]}</span>
                    <span className="arc-subtle">peak {Math.max(...regKnown.map((d) => d.online!))} · now {online ?? 0}</span>
                  </div>
                </>
              )}
          </div>
        </div>
        {can('node:read') && (
          <div className="arc-card">
            <CardHead title="Refused at the edge" sub="last 7 days"
              action={i?.security.bans_now !== null && i?.security.bans_now !== undefined ? (
                <Link to="/platform/sip-security" className="arc-btn arc-btn-sm">{i.security.bans_now} banned now</Link>
              ) : undefined} />
            <div className="arc-card-body">
              {!i ? <div className="arc-skeleton" style={{ height: 110 }} />
                : i.security.refusals === null ? <div className="arc-subtle" style={{ fontSize: 12 }}>No record from the switch.</div>
                : <DayBars data={i.security.refusals.map((r) => ({ day: r.day, a: r.sign_ins, b: r.edge }))}
                    series={{ a: { label: 'failed sign-ins', colour: 'var(--arc-warn)' }, b: { label: 'scanners and floods', colour: 'var(--arc-fg-4)' } }} />}
            </div>
          </div>
        )}
        {can('fraud:read') && (
          <div className="arc-card">
            <CardHead title="Fraud rules" sub="holds placed and alerts raised, per day"
              action={<Link to="/fraud" className="arc-btn arc-btn-sm">{i?.fraud ? `${i.fraud.open_holds} open` : 'Fraud'}</Link>} />
            <div className="arc-card-body">
              {!i || i.security.holds === null ? <div className="arc-skeleton" style={{ height: 110 }} />
                : <DayBars data={i.security.holds.map((r) => ({ day: r.day, a: r.holds, b: r.alerts }))}
                    series={{ a: { label: 'holds', colour: 'var(--arc-danger)' }, b: { label: 'alerts', colour: 'var(--arc-warn)' } }} />}
            </div>
          </div>
        )}
      </div>

      {/* The switch itself, its certificates and its mail. */}
      {(can('node:read') || can('billing:read')) && (
        <div className="arc-grid arc-grid-2" style={{ marginBottom: 14 }}>
          {can('node:read') && (
            <div className="arc-card">
              <CardHead title="Switch" sub="last heartbeat" action={<Link to="/platform/nodes" className="arc-btn arc-btn-sm">Nodes</Link>} />
              {!i ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 80 }} /></div>
                : !i.nodes || i.nodes.length === 0 ? <Empty icon="server" title="No nodes" />
                : i.nodes.map((n) => <NodeCard key={n.id} node={n} tz={tz} />)}
            </div>
          )}
          <div className="arc-stack" style={{ gap: 14 }}>
            {can('node:read') && (
              <div className="arc-card">
                <CardHead title="Certificates" sub={i?.certificates ? `checked ${relativeTime(i.certificates.checked_at)}` : undefined} />
                {!i ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 60 }} /></div>
                  : !i.certificates || i.certificates.items.length === 0 ? <Empty icon="shield" title="Not checked yet" hint="The certificate watch has not run." />
                  : (
                    <div className="arc-table-wrap">
                      <table className="arc-table">
                        <thead><tr><th>What</th><th>Name</th><th className="arc-td-num">Days left</th></tr></thead>
                        <tbody>
                          {i.certificates.items.map((x) => (
                            <tr key={`${x.label}-${x.name}`}>
                              <td style={{ fontWeight: 550 }}>{x.label}</td>
                              <td className="arc-mono" style={{ fontSize: 12 }}>{x.name ?? <span className="arc-subtle">{x.error ?? 'unreadable'}</span>}</td>
                              <td className="arc-td-num">
                                {x.days_left === null ? <Badge tone="muted">no reading</Badge>
                                  : <span style={{ color: `var(--arc-${certTone(x.days_left) === 'muted' ? 'fg-4' : certTone(x.days_left)})`, fontWeight: 550 }}>{x.days_left}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            )}
            {can('billing:read') && (
              <div className="arc-card">
                <CardHead title="Email outbox" action={<Link to="/platform/provider-keys" className="arc-btn arc-btn-sm">Sender</Link>} />
                <div className="arc-card-body">
                  {!i ? <div className="arc-skeleton" style={{ height: 40 }} />
                    : !i.email ? <span className="arc-subtle">No report.</span>
                    : (
                      <dl className="arc-kv" style={{ gridTemplateColumns: 'auto 1fr', margin: 0 }}>
                        <dt>Waiting</dt><dd className="arc-num">{i.email.waiting}</dd>
                        <dt>Retrying</dt><dd className="arc-num" style={i.email.retrying > 0 ? { color: 'var(--arc-warn)' } : undefined}>{i.email.retrying}</dd>
                        <dt>Failed this week</dt><dd className="arc-num" style={i.email.failed_7d > 0 ? { color: 'var(--arc-danger)' } : undefined}>{i.email.failed_7d}</dd>
                        <dt>Last failure</dt>
                        <dd>{i.email.last_failure ? <><span className="arc-mono" style={{ fontSize: 12 }}>{i.email.last_failure.error}</span> <span className="arc-subtle">· {relativeTime(i.email.last_failure.at)}</span></> : <span className="arc-subtle">none on record</span>}</dd>
                      </dl>
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * One switch, as its heartbeat describes it: whether it is beating, what it is carrying, how full
 * it is, which services run, which of its own checks fail, and whether Microsoft is answering it.
 */
function NodeCard({ node: n, tz }: { node: NonNullable<PlatformInsights['nodes']>[number]; tz: string }) {
  const beat = n.liveness === 'up' ? 'ok' : 'danger';
  const services = Object.entries(n.services);
  return (
    <div className="arc-card-body" style={{ display: 'grid', gap: 12 }}>
      <div className="arc-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div className="arc-row" style={{ gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{n.name}</span>
          <Badge tone={beat}>
            <span className={`arc-dot${n.liveness === 'up' ? ' arc-pulse' : ''}`} aria-hidden="true" />
            {n.liveness === 'up' ? 'beating' : n.liveness === 'down' ? 'silent' : 'never reported'}
          </Badge>
          {n.agent_version && <span className="arc-subtle arc-mono" style={{ fontSize: 11 }}>agent {n.agent_version}</span>}
        </div>
        <span className="arc-subtle" style={{ fontSize: 12 }}>
          {n.last_heartbeat_at ? `${relativeTime(n.last_heartbeat_at)} · up ${uptimeText(n.uptime_sec)}` : 'no heartbeat'}
        </span>
      </div>
      <div className="arc-row arc-num" style={{ gap: 14, fontSize: 12.5 }}>
        <span><strong>{formatNumber(n.active_calls)}</strong> <span className="arc-muted">calls up</span></span>
        <span><strong>{formatNumber(n.registrations)}</strong> <span className="arc-muted">phones registered</span></span>
      </div>
      <StatGroup>
        <Stat bare label="Load" value={n.load_1 === null ? '—' : n.load_1.toFixed(2)} sub={n.load_5 === null ? undefined : `${n.load_5.toFixed(2)} over 5 min`} />
        <Stat bare label="Memory" value={n.mem_pct === null ? '—' : `${n.mem_pct}%`} tone={shareTone(n.mem_pct)}
          sub={n.mem_total_mb ? `${n.mem_used_mb} of ${n.mem_total_mb} MB` : undefined} />
        <Stat bare label="Disk" value={n.disk_pct === null ? '—' : `${n.disk_pct}%`} tone={shareTone(n.disk_pct)}
          sub={n.cdr_spool_depth > 0 ? `${n.cdr_spool_depth} calls waiting to ship` : 'spool empty'} />
      </StatGroup>
      <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {services.length === 0 ? <span className="arc-subtle" style={{ fontSize: 12 }}>No service report.</span>
          : services.map(([name, state]) => (
            <Badge key={name} tone={state === 'active' ? 'ok' : 'danger'}>{name}{state === 'active' ? '' : ` · ${state}`}</Badge>
          ))}
      </div>
      {n.failing_assertions.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ margin: 0 }}>
          <Icon name="warning" size={14} />
          <div style={{ fontSize: 12 }}>
            <span style={{ fontWeight: 600 }}>{n.failing_assertions.length} check{n.failing_assertions.length === 1 ? '' : 's'} failing:</span> {n.failing_assertions.join(', ')}
          </div>
        </div>
      )}
      {n.teams && (
        <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
          <Icon name="teams" size={14} />
          <span style={{ fontWeight: 550 }}>Teams link</span>
          <Badge tone={n.teams.up === null ? 'muted' : n.teams.up ? 'ok' : 'danger'}>
            {n.teams.up === null ? 'no reading' : n.teams.up ? 'answering' : 'down'}
          </Badge>
          {n.teams.down_since && <span className="arc-subtle">since {formatDateTime(n.teams.down_since, tz)}</span>}
          <span className="arc-subtle arc-mono" style={{ fontSize: 11 }}>
            {n.teams.proxies.map((p) => `${p.proxy.split('.')[0]} ${p.status || 'no answer'}`).join(' · ')}
          </span>
        </div>
      )}
    </div>
  );
}

/** One row of the outcomes legend: swatch and label, count, share. */
function OutcomeRow({ swatch, label, value, share }: { swatch: string; label: string; value: string; share: string }) {
  return (
    <>
      <dt className="arc-row" style={{ gap: 6 }}><span className="arc-swatch" style={{ background: swatch }} />{label}</dt>
      <dd className="arc-num" style={{ textAlign: 'right' }}>{value}</dd>
      <dd className="arc-num arc-subtle" style={{ textAlign: 'right' }}>{share}</dd>
    </>
  );
}

/**
 * Who is on a call or ringing right now, across the platform (owner, 21 October 2026: "can we add
 * current live calls to the dashboard?"). The platform calls channel the Live page watches, so the
 * same scope check stands behind it and nothing is polled. One row per extension: an internal
 * call is both its ends.
 */
function LiveNow({ names }: { names: Map<string, string> }) {
  const { extensions, nodes, streamed, skewMs } = useLiveState(['calls']);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);
  const active = extensions.filter((e) => e.state !== 'idle').sort((a, b) => a.since - b.since);
  const onCall = active.filter((e) => e.state === 'on_call').length;
  const ringing = active.length - onCall;

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Live now"
        sub={streamed ? <span className="arc-num">{onCall} on a call · {ringing} ringing</span> : undefined}
        action={<Link to="/live" className="arc-btn arc-btn-sm">Live view</Link>} />
      {!streamed ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 40 }} /></div>
        : nodes === 0 ? <Empty icon="warning" title="No report from the switch" hint="Live state is unknown until a node reports." />
        : active.length === 0 ? <div className="arc-card-body arc-subtle">No calls right now.</div>
        : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Extension</th><th>Account</th><th>State</th><th>With</th><th className="arc-td-num">Time</th></tr>
              </thead>
              <tbody>
                {active.slice(0, 10).map((e) => (
                  <tr key={`${e.domain}|${e.user}`}>
                    <td className="arc-mono">{e.user}</td>
                    <td>{(e.tenant_id && names.get(e.tenant_id)) ?? e.domain}</td>
                    <td>
                      {e.state === 'ringing'
                        ? <Badge tone="warn"><span className="arc-dot arc-pulse" aria-hidden="true" />ringing</Badge>
                        : <Badge tone="info"><span className="arc-dot" aria-hidden="true" />on a call{e.direction ? ` · ${e.direction}` : ''}</Badge>}
                    </td>
                    <td className="arc-mono">{e.peer ?? '—'}</td>
                    <td className="arc-td-num">{formatDuration(Math.max(0, Math.floor((now + skewMs - e.since) / 1000)))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {active.length > 10 && (
              <div className="arc-card-body arc-subtle" style={{ fontSize: 12 }}>and {active.length - 10} more in the live view</div>
            )}
          </div>
        )}
    </div>
  );
}
